import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  useIsAuthenticated,
  useDashboardInfo,
  useAppDispatch,
  getDashboardInfo,
} from "../redux";
import { planHasFeature } from "../utils/planApiAccess";
import {
  PLAYGROUND_API_BASE,
  PLAYGROUND_ENDPOINTS,
  groupPlaygroundEndpoints,
} from "../utils/playgroundEndpoints";
import "./ApiPlayground.css";

const maskKey = (key) => {
  if (!key || key.length < 10) return key || "YOUR_API_KEY";
  return `${key.slice(0, 8)}…${key.slice(-4)}`;
};

const guideForEndpoint = (endpoint) => {
  if (endpoint.responseGuide?.length) return endpoint.responseGuide;
  return (endpoint.outputParams || []).map((p) => ({
    name: p.name,
    meaning: p.description,
  }));
};

const ApiPlayground = () => {
  const isAuthenticated = useIsAuthenticated();
  const dashboardInfo = useDashboardInfo();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialId =
    searchParams.get("endpoint") || PLAYGROUND_ENDPOINTS[0].id;
  const [activeId, setActiveId] = useState(initialId);
  const [mode, setMode] = useState("details");
  const [paramValues, setParamValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState(null);
  const [responseBody, setResponseBody] = useState(null);
  const [responseError, setResponseError] = useState(null);

  const endpoint =
    PLAYGROUND_ENDPOINTS.find((e) => e.id === activeId) ||
    PLAYGROUND_ENDPOINTS[0];

  const apiKey = dashboardInfo?.apiKey || "";
  const userPlan = String(
    dashboardInfo?.subscriptionPlan || dashboardInfo?.plan || "NONE"
  ).toUpperCase();

  const hasPlanAccess = planHasFeature(userPlan, endpoint.accessFeature);
  const canSendHttp =
    isAuthenticated && hasPlanAccess && Boolean(apiKey);

  const grouped = useMemo(
    () => groupPlaygroundEndpoints(PLAYGROUND_ENDPOINTS),
    []
  );
  const fieldGuide = useMemo(() => guideForEndpoint(endpoint), [endpoint]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getDashboardInfo());
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    const defaults = {};
    (endpoint.pathParams || []).forEach((p) => {
      defaults[p.key] = p.defaultValue || "";
    });
    (endpoint.queryParams || []).forEach((p) => {
      defaults[p.key] = p.defaultValue || "";
    });
    setParamValues(defaults);
    setResponseStatus(null);
    setResponseBody(null);
    setResponseError(null);
  }, [endpoint]);

  useEffect(() => {
    setSearchParams({ endpoint: endpoint.id }, { replace: true });
  }, [endpoint.id, setSearchParams]);

  const displayKey = isAuthenticated && apiKey ? apiKey : "YOUR_API_KEY";
  const maskedDisplayKey =
    isAuthenticated && apiKey ? maskKey(apiKey) : "YOUR_API_KEY";

  const builtPath = useMemo(() => {
    if (endpoint.buildPath) return endpoint.buildPath(paramValues);
    return endpoint.pathTemplate;
  }, [endpoint, paramValues]);

  const queryString = useMemo(() => {
    const parts = [`apiKey=${encodeURIComponent(displayKey)}`];
    (endpoint.queryParams || []).forEach((p) => {
      const val = paramValues[p.key];
      if (val != null && String(val).trim() !== "") {
        parts.push(`${p.key}=${encodeURIComponent(String(val).trim())}`);
      }
    });
    return parts.join("&");
  }, [endpoint, paramValues, displayKey]);

  const fullUrl = `${PLAYGROUND_API_BASE}${builtPath}?${queryString}`;
  const schemaUrl = `${PLAYGROUND_API_BASE}${endpoint.pathTemplate}?apiKey={apiKey}${
    (endpoint.queryParams || []).length
      ? `&${endpoint.queryParams.map((p) => `${p.key}={${p.key}}`).join("&")}`
      : ""
  }`;

  const exampleUrl = `${PLAYGROUND_API_BASE}${
    endpoint.buildPath
      ? endpoint.buildPath(
          Object.fromEntries(
            [
              ...(endpoint.pathParams || []),
              ...(endpoint.queryParams || []),
            ].map((p) => [p.key, p.defaultValue])
          )
        )
      : endpoint.pathTemplate
  }?apiKey=YOUR_API_KEY${(endpoint.queryParams || [])
    .map((p) => `&${p.key}=${encodeURIComponent(p.defaultValue || "")}`)
    .join("")}`;

  const handleSend = async () => {
    if (!canSendHttp) return;

    setLoading(true);
    setResponseError(null);
    setResponseBody(null);
    setResponseStatus(null);

    try {
      const qs = [`apiKey=${encodeURIComponent(apiKey)}`];
      (endpoint.queryParams || []).forEach((p) => {
        const val = paramValues[p.key];
        if (val != null && String(val).trim() !== "") {
          qs.push(`${p.key}=${encodeURIComponent(String(val).trim())}`);
        }
      });
      const url = `${PLAYGROUND_API_BASE}${builtPath}?${qs.join("&")}`;
      const res = await fetch(url, { method: endpoint.method || "GET" });
      const text = await res.text();
      let parsed = text;
      try {
        parsed = JSON.parse(text);
      } catch {
        /* keep text */
      }
      setResponseStatus(res.status);
      setResponseBody(parsed);
      if (!res.ok) {
        setResponseError(
          typeof parsed === "object"
            ? parsed.error || parsed.details || `Request failed (${res.status})`
            : `Request failed (${res.status})`
        );
      }
    } catch (err) {
      setResponseError(err.message || "Network error");
    } finally {
      setLoading(false);
    }
  };

  const sendBlockedReason = () => {
    if (!isAuthenticated) {
      return "Please login to use the API Playground.";
    }
    if (!hasPlanAccess) {
      return `Your current plan (${userPlan === "NONE" ? "none" : userPlan}) cannot call this endpoint. Requires ${endpoint.minPlan}+.`;
    }
    if (!apiKey) return "No API key found on your account.";
    return null;
  };

  return (
    <div className="api-playground-page">
      <div className="ap-hero">
        <div className="ap-hero-inner">
          <div>
            <p className="ap-eyebrow">API PLAYGROUND</p>
            <h1>Explore every Forex API endpoint</h1>
            <p className="ap-hero-sub">
              Browse all endpoints, inspect samples, and send live requests.
              Access follows your active subscription — Free includes all three
              Snapshot URLs.
            </p>
            {isAuthenticated ? (
              <p className="ap-plan-chip">
                Active plan:{" "}
                <strong>{userPlan === "NONE" ? "None" : userPlan}</strong>
              </p>
            ) : (
              <p className="ap-login-msg">
                Please login to use the API Playground.{" "}
                <Link to="/login?redirect=/api-playground">Login</Link>
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="ap-shell">
        <aside className="ap-sidebar">
          <p className="ap-sidebar-label">Endpoints</p>
          {grouped.map((group) => (
            <div key={group.name} className="ap-nav-group">
              <p className="ap-nav-group-title">{group.name}</p>
              <ul>
                {group.items.map((ep) => {
                  const unlocked =
                    !isAuthenticated ||
                    planHasFeature(userPlan, ep.accessFeature);
                  return (
                    <li key={ep.id}>
                      <button
                        type="button"
                        className={ep.id === endpoint.id ? "active" : ""}
                        onClick={() => setActiveId(ep.id)}
                      >
                        <span
                          className={`ap-dot ${unlocked ? "ok" : "locked"}`}
                          aria-hidden="true"
                        />
                        <span>
                          <strong>{ep.shortName}</strong>
                          <small>
                            {ep.minPlan}+
                            {!unlocked && isAuthenticated ? " · locked" : ""}
                          </small>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </aside>

        <main className="ap-main">
          <div className="ap-endpoint-head">
            <div>
              <div className="ap-meta-row">
                <span className="ap-method">{endpoint.method}</span>
                <span className="ap-plan">{endpoint.minPlan}+</span>
                {isAuthenticated && (
                  <span
                    className={`ap-access ${hasPlanAccess ? "yes" : "no"}`}
                  >
                    {hasPlanAccess
                      ? "Available on your plan"
                      : "Upgrade required"}
                  </span>
                )}
              </div>
              <h2>{endpoint.name}</h2>
              <p>{endpoint.description}</p>
            </div>
            <div className="ap-mode-toggle" role="tablist">
              <button
                type="button"
                role="tab"
                className={mode === "details" ? "active" : ""}
                onClick={() => setMode("details")}
              >
                Details
              </button>
              <button
                type="button"
                role="tab"
                className={mode === "playground" ? "active" : ""}
                onClick={() => setMode("playground")}
              >
                Playground
              </button>
            </div>
          </div>

          {mode === "details" ? (
            <div className="ap-details-stack">
              <h3>URL Schema</h3>
              <pre className="ap-code">{schemaUrl}</pre>
              <h3>Input Parameters</h3>
              <table className="ap-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Meaning</th>
                  </tr>
                </thead>
                <tbody>
                  {endpoint.inputParams.map((p) => (
                    <tr key={p.name}>
                      <td>
                        <code>
                          {p.required ? "* " : ""}
                          {p.name}
                        </code>
                      </td>
                      <td>{p.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <h3>Example Request</h3>
              <pre className="ap-code">{exampleUrl}</pre>
              <h3>Response fields explained</h3>
              <table className="ap-table">
                <thead>
                  <tr>
                    <th>Variable</th>
                    <th>Meaning</th>
                  </tr>
                </thead>
                <tbody>
                  {fieldGuide.map((row) => (
                    <tr key={row.name}>
                      <td>
                        <code>{row.name}</code>
                      </td>
                      <td>{row.meaning}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <h3>Example Output</h3>
              <pre className="ap-code">
                {JSON.stringify(endpoint.sampleOutput, null, 2)}
              </pre>
            </div>
          ) : (
            <div className="ap-playground-layout">
              <div className="ap-playground-top">
                <div className="ap-request-card">
                  <h3>Request</h3>
                  {(endpoint.pathParams || []).map((p) => (
                    <label key={p.key} className="ap-field">
                      <span>{p.label}</span>
                      <input
                        type="text"
                        value={paramValues[p.key] || ""}
                        onChange={(e) =>
                          setParamValues((prev) => ({
                            ...prev,
                            [p.key]: e.target.value,
                          }))
                        }
                      />
                    </label>
                  ))}
                  {(endpoint.queryParams || []).map((p) => (
                    <label key={p.key} className="ap-field">
                      <span>{p.label}</span>
                      <input
                        type="text"
                        value={paramValues[p.key] || ""}
                        onChange={(e) =>
                          setParamValues((prev) => ({
                            ...prev,
                            [p.key]: e.target.value,
                          }))
                        }
                      />
                    </label>
                  ))}
                  <label className="ap-field">
                    <span>apiKey</span>
                    <input
                      type="text"
                      readOnly
                      value={
                        isAuthenticated
                          ? maskedDisplayKey
                          : "Login required — YOUR_API_KEY"
                      }
                    />
                  </label>
                  <pre className="ap-code ap-url-preview">
                    {fullUrl.replace(displayKey, maskedDisplayKey)}
                  </pre>
                  {canSendHttp ? (
                    <button
                      type="button"
                      className="ap-send-btn"
                      onClick={handleSend}
                      disabled={loading}
                    >
                      {loading ? "Sending…" : "Send Request"}
                    </button>
                  ) : (
                    <div className="ap-locked">
                      <p>{sendBlockedReason()}</p>
                      {!isAuthenticated ? (
                        <Link
                          className="ap-send-btn"
                          to="/login?redirect=/api-playground"
                        >
                          Login
                        </Link>
                      ) : !hasPlanAccess ? (
                        <Link
                          className="ap-send-btn secondary"
                          to="/plans&pricing"
                        >
                          View Plans & Pricing
                        </Link>
                      ) : null}
                    </div>
                  )}
                </div>

                <div className="ap-guide-card">
                  <h3>Response fields explained</h3>
                  <p className="ap-guide-intro">
                    What each variable in this API response means:
                  </p>
                  <ul className="ap-guide-list">
                    {fieldGuide.map((row) => (
                      <li key={row.name}>
                        <code>{row.name}</code>
                        <span>{row.meaning}</span>
                      </li>
                    ))}
                  </ul>
                  <h4>Request parameters</h4>
                  <ul className="ap-guide-list">
                    {endpoint.inputParams.map((p) => (
                      <li key={p.name}>
                        <code>
                          {p.required ? "* " : ""}
                          {p.name}
                        </code>
                        <span>{p.description}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="ap-response-card">
                <h3>
                  Response
                  {responseStatus != null && (
                    <span
                      className={`ap-status ${
                        responseStatus >= 200 && responseStatus < 300
                          ? "ok"
                          : "err"
                      }`}
                    >
                      {responseStatus}
                    </span>
                  )}
                </h3>
                {responseError && (
                  <p className="ap-error-msg">{responseError}</p>
                )}
                <pre className="ap-code ap-response-body">
                  {responseBody != null
                    ? typeof responseBody === "string"
                      ? responseBody
                      : JSON.stringify(responseBody, null, 2)
                    : "// Live JSON appears here after you click Send Request"}
                </pre>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ApiPlayground;
